/**
 * CriticalSettingsPage — System · Critical Settings.
 *
 * Purpose-built panel for the "load-bearing" SystemSettings rows whose
 * values change the way the entire admin-panel + Function pipeline behaves.
 * The generic key/value grid at /system/settings still exposes every row;
 * this page exists so officers don't have to type-search the grid to flip
 * the things that matter most:
 *
 *   1. identity.source.groupId          — string,  EDITABLE (Entra picker)
 *   2. identity.ddp.enabled             — bool,    EDITABLE (LN DDP kill-switch)
 *   3. identity.ddp.endpointBase        — string,  EDITABLE (LN DDP REST base URL)
 *   4. identity.ddp.enabledModes        — string,  EDITABLE (CSV of cart modes; narrows the kill-switch)
 *   5. identity.ddp.orgId.{prod,sandbox}     — string, EDITABLE (LN org id PER ENVIRONMENT)
 *   5b. identity.ddp.apiKeyRef.{prod,sandbox} — string, EDITABLE (config-key NAME per environment)
 *   6. identity.ddp.policyName          — string,  EDITABLE (LN multi-product Policy name)
 *   7. identity.idExpirationWarningDays — int,     EDITABLE
 *   8. admin.auth.allowBypassKey        — bool,    READ-ONLY (display only)
 *   9. notifications.teamsBotAppId      — string,  READ-ONLY (display only)
 *
 * Wire contract:
 *   - Read:  GET  /v3/config/system-settings/rows   (ConfigFunction.cs:361)
 *   - Write: POST /v3/admin/system-settings         (AdminSystemFunction.cs:84)
 *   - Entra: GET  /v3/admin/entra/groups?q=...      (AdminFunction.cs:1439)
 *
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL → buildAdminAuthHeaders OMITS the bypass-
 * key header, letting the Function-side AdminAuthService resolve via the
 * MSAL Path B bearer (preferred) or Path C officer-code header fallback per
 * `project_admin_auth_allowbypasskey_off_20260514`. No new PUT route needed
 * — POST upsert covers create + update through the same gate.
 *
 * Read-only keys (admin.auth.allowBypassKey, notifications.teamsBotAppId)
 * are intentionally not exposed as editable here. They are flipped via the
 * generic /system/settings grid (or runSeedSql for emergency rollback) by
 * deliberate ceremony — surfacing them as a one-click toggle on a routine
 * admin page would invite the wrong incident.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type SystemSettingUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { AuditChips, Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { BoolField, NumberField, TextField } from '@/components/FormField';
import { EntraGroupPicker } from '@/components/EntraPicker';

interface SystemSettingRow {
  settingKey: string;
  settingValue: string | null;
  dataType: string;
  categoryCode: string;
  isSensitive: boolean;
  description: string | null;
  defaultValue: string | null;
  isActive: boolean;
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

type CriticalKey =
  | 'identity.source.groupId'
  | 'identity.ddp.enabled'
  | 'identity.ddp.enabledModes'
  | 'identity.ddp.endpointBase'
  | 'identity.ddp.orgId.prod'
  | 'identity.ddp.orgId.sandbox'
  | 'identity.ddp.apiKeyRef.prod'
  | 'identity.ddp.apiKeyRef.sandbox'
  | 'identity.ddp.policyName'
  | 'identity.ddp.serviceType'
  | 'identity.ddp.eventType'
  | 'identity.idExpirationWarningDays'
  | 'admin.auth.allowBypassKey'
  | 'notifications.teamsBotAppId';

const CRITICAL_KEYS: ReadonlyArray<CriticalKey> = [
  'identity.source.groupId',
  'identity.ddp.enabled',
  'identity.ddp.enabledModes',
  'identity.ddp.endpointBase',
  'identity.ddp.orgId.prod',
  'identity.ddp.orgId.sandbox',
  'identity.ddp.apiKeyRef.prod',
  'identity.ddp.apiKeyRef.sandbox',
  'identity.ddp.policyName',
  'identity.ddp.serviceType',
  'identity.ddp.eventType',
  'identity.idExpirationWarningDays',
  'admin.auth.allowBypassKey',
  'notifications.teamsBotAppId',
];

interface SettingMeta {
  key: CriticalKey;
  label: string;
  blurb: string;
  /** When true, value cannot be changed here — use /system/settings instead. */
  readOnly: boolean;
  /** Default to display when the row is missing entirely. */
  defaultValue: string;
  /** Optional human-friendly note explaining the default. */
  defaultBlurb?: string;
}

const META: Record<CriticalKey, SettingMeta> = {
  'identity.source.groupId': {
    key: 'identity.source.groupId',
    label: 'Identity source group',
    blurb:
      'Entra Object ID of the mail-enabled security group whose direct members are the identity source for the Add-User picker and the sync surface. Renders as a live Entra group picker — never paste a raw GUID.',
    readOnly: false,
    defaultValue: '84fa6a90-024b-4e39-a016-270602378438',
    defaultBlurb: 'Default = AllOwners@mypcb.com (84fa6a90-024b-4e39-a016-270602378438).',
  },
  'identity.ddp.enabled': {
    key: 'identity.ddp.enabled',
    label: 'LexisNexis DDP API (identity verification)',
    blurb:
      'MASTER kill-switch for the LexisNexis DDP call (InstantID + FraudPoint in one round-trip, billed per call). Off = the Verify-tab dispatcher falls through to the html-import path (LN website + extension), which is what the bank used before this went live. On = DDP runs, but only for the cart modes listed in identity.ddp.enabledModes.',
    readOnly: false,
    defaultValue: 'false',
  },
  'identity.ddp.enabledModes': {
    key: 'identity.ddp.enabledModes',
    label: 'LN DDP — cart modes where it is live',
    blurb:
      'Comma-separated cart modes. NARROWS the master switch and can never widen it. EMPTY MEANS EVERY MODE. Cart mode picks the LexisNexis institution: Test-Live goes to the sandbox org, Production and UAT-Live to the production org (real customers, real billing). Set to "Test-Live" alone to exercise DDP against the sandbox without arming it for real customers.',
    readOnly: false,
    defaultValue: 'Test-Live',
    defaultBlurb: 'Default "Test-Live" = sandbox institution only.',
  },
  'identity.ddp.endpointBase': {
    key: 'identity.ddp.endpointBase',
    label: 'LN DDP endpoint base URL',
    blurb:
      'Base host of the DDP attribute-query endpoint. The adapter appends "/api/attribute-query". Note this is a ThreatMetrix host (online-metrix.net), NOT a lexisnexis.com host, so it is unaffected by the October 2026 lexisnexisrisk.com domain transition.',
    readOnly: false,
    defaultValue: 'https://h-api.online-metrix.net',
    defaultBlurb: 'West/Sacramento endpoint — the host in the PCB Postman collection from LNRS.',
  },
  'identity.ddp.orgId.prod': {
    key: 'identity.ddp.orgId.prod',
    label: 'LN DDP org id — PRODUCTION',
    blurb:
      'The org_id sent as a mandatory BODY attribute for Production / UAT-Live carts. LexisNexis issues a DISTINCT org id per environment; using one pair for both would send production credentials to the test institution.',
    readOnly: false,
    defaultValue: '',
    defaultBlurb: 'Issued by LNRS with the contract.',
  },
  'identity.ddp.orgId.sandbox': {
    key: 'identity.ddp.orgId.sandbox',
    label: 'LN DDP org id — SANDBOX',
    blurb:
      'The org_id used for Test-Live carts (the sandbox institution). Sandbox-mode carts never reach LexisNexis at all — they are served from a fixture.',
    readOnly: false,
    defaultValue: '',
  },
  'identity.ddp.apiKeyRef.prod': {
    key: 'identity.ddp.apiKeyRef.prod',
    label: 'LN DDP API key reference — PRODUCTION (config-key NAME)',
    blurb:
      'The NAME of the app setting / Key Vault reference holding the production API key — never the key itself. The adapter resolves this name through IConfiguration so no secret is ever written to SystemSettings, which is readable through the admin SQL routes. Watch for an UNRESOLVED Key Vault reference: it surfaces as the literal "@Microsoft.KeyVault(...)" string and would be sent to LexisNexis as the api_key. /api/health/dependencies now reports that as a ddp fault.',
    readOnly: false,
    defaultValue: 'DdpApiKeyProd',
  },
  'identity.ddp.apiKeyRef.sandbox': {
    key: 'identity.ddp.apiKeyRef.sandbox',
    label: 'LN DDP API key reference — SANDBOX (config-key NAME)',
    blurb:
      'Same as the production reference but for Test-Live carts. Rotating either secret is a Key Vault operation; nothing changes here.',
    readOnly: false,
    defaultValue: 'DdpApiKeySandbox',
  },
  'identity.ddp.policyName': {
    key: 'identity.ddp.policyName',
    label: 'LN DDP Policy name',
    blurb:
      'Which DDP Policy to invoke, sent as the "policy" attribute. Verified against the PCB Postman collection from LNRS and live sandbox calls: the value is "default". Change only if LNRS provisions a differently named Policy for this org.',
    readOnly: false,
    defaultValue: 'default',
    defaultBlurb: 'Verified live 2026-09-09 — "default".',
  },
  'identity.ddp.serviceType': {
    key: 'identity.ddp.serviceType',
    label: 'LN DDP service type',
    blurb:
      'The "service_type" attribute. "basic" is the value in the LNRS Postman collection and the one proven against the sandbox org.',
    readOnly: false,
    defaultValue: 'basic',
  },
  'identity.ddp.eventType': {
    key: 'identity.ddp.eventType',
    label: 'LN DDP event type',
    blurb:
      'The "event_type" attribute, telling LexisNexis what the query is for. "account_creation" matches the deposit-account-opening use case.',
    readOnly: false,
    defaultValue: 'account_creation',
  },
  'identity.idExpirationWarningDays': {
    key: 'identity.idExpirationWarningDays',
    label: 'ID expiration warning (days)',
    blurb:
      'How many days before a customer’s identification document expires the FSR sees an inline expiration warning during intake. 0 disables the warning entirely.',
    readOnly: false,
    defaultValue: '30',
  },
  'admin.auth.allowBypassKey': {
    key: 'admin.auth.allowBypassKey',
    label: 'Bypass-key auth allowed',
    blurb:
      'Master switch for the legacy x-config-seed-bypass header path. When false, ONLY a valid Teams SSO bearer token (Path B) or officer-code header (Path C) is accepted on typed admin routes. Read-only here — flip via /system/settings deliberately.',
    readOnly: true,
    defaultValue: 'true',
  },
  'notifications.teamsBotAppId': {
    key: 'notifications.teamsBotAppId',
    label: 'Teams bot app ID',
    blurb:
      'Application (client) ID of the Teams bot used for DM dispatch by the notification adapter. While blank, the Teams DM adapter cannot send messages. Read-only here — set deliberately via /system/settings, since rotating the bot app ID requires a coordinated re-grant.',
    readOnly: true,
    defaultValue: '',
  },
};

interface DraftState {
  /** The string we send to upsert.settingValue. */
  value: string;
  /** Whether the user has edited away from the persisted value. */
  dirty: boolean;
  /** Last save error scoped to this row. */
  saveError: string | null;
  /** Currently saving. */
  saving: boolean;
  /** Last successful save message (cleared on next edit). */
  savedNote: string | null;
}

function emptyDraft(persisted: string): DraftState {
  return { value: persisted, dirty: false, saveError: null, saving: false, savedNote: null };
}

function parseBool(v: string | null | undefined): boolean {
  if (!v) return false;
  const n = v.trim().toLowerCase();
  return n === 'true' || n === '1' || n === 'yes' || n === 'on';
}

function parseInteger(v: string | null | undefined): number | null {
  if (v == null) return null;
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

/** Render value as a small mono chip, with sensible empty / unset display. */
function ValueChip({ value, tone }: { value: string; tone?: 'ok' | 'warn' | 'muted' }): JSX.Element {
  if (value === '') {
    return <Pill tone="muted">(unset)</Pill>;
  }
  return <Pill tone={tone ?? 'muted'}>{value}</Pill>;
}

export function CriticalSettingsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<SystemSettingRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Partial<Record<CriticalKey, DraftState>>>({});
  const [refreshNonce, setRefreshNonce] = useState(0);

  // ── Load ───────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.systemSettingsRows();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRows(resp.data as unknown as ReadonlyArray<SystemSettingRow>);
        } else {
          setLoadError(resp.error ?? 'Failed to load system settings.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  // Map: key → row (may be undefined when the row isn't seeded yet).
  const rowByKey = useMemo(() => {
    const m = new Map<CriticalKey, SystemSettingRow>();
    if (rows) {
      for (const r of rows) {
        if ((CRITICAL_KEYS as ReadonlyArray<string>).includes(r.settingKey)) {
          m.set(r.settingKey as CriticalKey, r);
        }
      }
    }
    return m;
  }, [rows]);

  // Reset drafts to match the latest persisted values whenever the rows reload.
  useEffect(() => {
    if (!rows) return;
    const next: Partial<Record<CriticalKey, DraftState>> = {};
    for (const key of CRITICAL_KEYS) {
      const r = rowByKey.get(key);
      const persisted = (r?.settingValue ?? META[key].defaultValue) ?? '';
      next[key] = emptyDraft(persisted);
    }
    setDrafts(next);
  }, [rows, rowByKey]);

  const getDraft = (key: CriticalKey): DraftState => drafts[key] ?? emptyDraft(META[key].defaultValue);

  const updateDraft = useCallback((key: CriticalKey, next: Partial<DraftState>) => {
    setDrafts((d) => ({ ...d, [key]: { ...(d[key] ?? emptyDraft(META[key].defaultValue)), ...next } }));
  }, []);

  const onValueChange = useCallback(
    (key: CriticalKey, newValue: string) => {
      const persisted = (rowByKey.get(key)?.settingValue ?? META[key].defaultValue) ?? '';
      updateDraft(key, {
        value: newValue,
        dirty: newValue !== persisted,
        saveError: null,
        savedNote: null,
      });
    },
    [rowByKey, updateDraft],
  );

  const onSave = useCallback(
    async (key: CriticalKey) => {
      const draft = getDraft(key);
      const row = rowByKey.get(key);
      if (!draft.dirty) return;

      // Validate by type before round-tripping.
      if (key === 'identity.idExpirationWarningDays') {
        const n = parseInteger(draft.value);
        if (n === null || n < 0 || n > 3650) {
          updateDraft(key, { saveError: 'Enter a non-negative integer up to 3650 (10 years).' });
          return;
        }
      }
      if (key === 'identity.source.groupId') {
        if (!/^[0-9a-fA-F-]{36}$/.test(draft.value.trim())) {
          updateDraft(key, { saveError: 'Select a group from the picker. The value must be an Entra Object ID (GUID).' });
          return;
        }
      }

      updateDraft(key, { saving: true, saveError: null, savedNote: null });

      // Compose upsert body. For existing rows, leave optional fields untouched
      // (Function falls through to the persisted column on partial PUTs). For
      // new rows, the Function applies safe defaults (DataType='string',
      // CategoryCode='general', IsActive=true) — we provide DataType +
      // CategoryCode explicitly so the row lands in the right bucket.
      const body: SystemSettingUpsertBody = row
        ? { settingKey: key, settingValue: draft.value }
        : {
            settingKey: key,
            settingValue: draft.value,
            dataType: dataTypeFor(key),
            categoryCode: categoryFor(key),
            description: META[key].blurb,
            defaultValue: META[key].defaultValue,
            isActive: true,
            isSensitive: false,
          };

      try {
        const resp = await client.retail.admin.systemSettings.upsert(ADMIN_AUTH_TOKEN_SENTINEL, body);
        if (!resp.success) {
          updateDraft(key, { saving: false, saveError: resp.error ?? 'Upsert returned success=false.' });
          return;
        }
        updateDraft(key, { saving: false, savedNote: 'Saved.' });
        refresh();
      } catch (e) {
        updateDraft(key, {
          saving: false,
          saveError: ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        });
      }
    },
    [client, rowByKey, drafts, updateDraft, refresh],
  );

  const onRevert = useCallback(
    (key: CriticalKey) => {
      const persisted = (rowByKey.get(key)?.settingValue ?? META[key].defaultValue) ?? '';
      updateDraft(key, { value: persisted, dirty: false, saveError: null, savedNote: null });
    },
    [rowByKey, updateDraft],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load system settings: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading critical settings…" />;

  return (
    <>
      <Banner tone="info">
        These rows change behavior bank-wide. Edits take effect immediately on the next request — there is no rollout
        window. Read-only rows are routed here for visibility only; flip them deliberately via{' '}
        <a href="#/system/settings">/system/settings</a>.
      </Banner>

      <div className="critical-settings__stack">
        {CRITICAL_KEYS.map((key) => (
          <SettingCard
            key={key}
            meta={META[key]}
            row={rowByKey.get(key)}
            draft={getDraft(key)}
            onValueChange={(v) => onValueChange(key, v)}
            onSave={() => void onSave(key)}
            onRevert={() => onRevert(key)}
          />
        ))}
      </div>
    </>
  );
}

// ─── Per-card renderer ─────────────────────────────────────────────────────

interface SettingCardProps {
  meta: SettingMeta;
  row: SystemSettingRow | undefined;
  draft: DraftState;
  onValueChange: (newValue: string) => void;
  onSave: () => void;
  onRevert: () => void;
}

function SettingCard(props: SettingCardProps): JSX.Element {
  const { meta, row, draft } = props;
  const persistedValue = (row?.settingValue ?? meta.defaultValue) ?? '';
  const titleSuffix = row ? null : <Pill tone="warn">unseeded</Pill>;

  return (
    <Card
      title={
        <span className="critical-settings__title">
          <span>{meta.label}</span>
          <code className="critical-settings__key">{meta.key}</code>
          {meta.readOnly && <Pill tone="muted">read-only</Pill>}
          {titleSuffix}
        </span>
      }
    >
      <p className="critical-settings__blurb">{meta.blurb}</p>

      {meta.readOnly ? (
        <ReadOnlyValueRow meta={meta} persistedValue={persistedValue} />
      ) : (
        <EditableValueRow
          meta={meta}
          persistedValue={persistedValue}
          draft={draft}
          onValueChange={props.onValueChange}
          onSave={props.onSave}
          onRevert={props.onRevert}
        />
      )}

      {row && (
        <div className="critical-settings__audit">
          <AuditChips
            createdAt={row.createdAt}
            createdBy={row.createdBy}
            updatedAt={row.updatedAt}
            updatedBy={row.updatedBy}
          />
        </div>
      )}
    </Card>
  );
}

function ReadOnlyValueRow({
  meta,
  persistedValue,
}: {
  meta: SettingMeta;
  persistedValue: string;
}): JSX.Element {
  if (meta.key === 'admin.auth.allowBypassKey') {
    const on = parseBool(persistedValue);
    return (
      <div className="critical-settings__row">
        <span className="critical-settings__row-label">Current value</span>
        <Pill tone={on ? 'warn' : 'ok'}>{on ? 'allowed (legacy)' : 'denied'}</Pill>
      </div>
    );
  }
  return (
    <div className="critical-settings__row">
      <span className="critical-settings__row-label">Current value</span>
      <ValueChip value={persistedValue} />
    </div>
  );
}

function EditableValueRow(props: {
  meta: SettingMeta;
  persistedValue: string;
  draft: DraftState;
  onValueChange: (newValue: string) => void;
  onSave: () => void;
  onRevert: () => void;
}): JSX.Element {
  const { meta, persistedValue, draft } = props;

  return (
    <div className="critical-settings__editor">
      {meta.key === 'identity.source.groupId' && (
        <EntraGroupPicker
          value={
            draft.value && /^[0-9a-fA-F-]{36}$/.test(draft.value)
              ? { id: draft.value, displayName: draft.value }
              : null
          }
          onChange={(g) => props.onValueChange(g?.id ?? '')}
          placeholder="Search Entra groups by name…"
        />
      )}

      {meta.key === 'identity.ddp.enabled' && (
        <BoolField
          label="DDP API enabled"
          value={parseBool(draft.value)}
          onChange={(b) => props.onValueChange(b ? 'true' : 'false')}
          hint="Off = Verify-tab dispatcher falls through to html-import (extension + bookmarklet). On = routes to LexisNexis DDP REST API."
        />
      )}

      {meta.key === 'identity.ddp.enabledModes' && (
        <TextField
          label="Cart modes where DDP is live (CSV; empty = all)"
          value={draft.value}
          onChange={props.onValueChange}
          placeholder="Test-Live"
          maxLength={200}
          monospace
        />
      )}

      {meta.key === 'identity.ddp.endpointBase' && (
        <TextField
          label="Endpoint base URL"
          value={draft.value}
          onChange={props.onValueChange}
          placeholder="https://h-api.online-metrix.net"
          maxLength={400}
          monospace
        />
      )}

      {(meta.key === 'identity.ddp.orgId.prod' || meta.key === 'identity.ddp.orgId.sandbox') && (
        <TextField
          label={meta.key.endsWith('.prod') ? 'Org id (PRODUCTION)' : 'Org id (SANDBOX)'}
          value={draft.value}
          onChange={props.onValueChange}
          placeholder="issued by LexisNexis"
          maxLength={64}
          monospace
        />
      )}

      {(meta.key === 'identity.ddp.apiKeyRef.prod' || meta.key === 'identity.ddp.apiKeyRef.sandbox') && (
        <TextField
          label={meta.key.endsWith('.prod')
            ? 'API key reference — PRODUCTION (config-key NAME, not the key)'
            : 'API key reference — SANDBOX (config-key NAME, not the key)'}
          value={draft.value}
          onChange={props.onValueChange}
          placeholder={meta.key.endsWith('.prod') ? 'DdpApiKeyProd' : 'DdpApiKeySandbox'}
          maxLength={128}
          monospace
        />
      )}

      {meta.key === 'identity.ddp.policyName' && (
        <TextField
          label="Policy name"
          value={draft.value}
          onChange={props.onValueChange}
          placeholder="default"
          maxLength={64}
          monospace
        />
      )}

      {meta.key === 'identity.ddp.serviceType' && (
        <TextField
          label="Service type"
          value={draft.value}
          onChange={props.onValueChange}
          placeholder="basic"
          maxLength={64}
          monospace
        />
      )}

      {meta.key === 'identity.ddp.eventType' && (
        <TextField
          label="Event type"
          value={draft.value}
          onChange={props.onValueChange}
          placeholder="account_creation"
          maxLength={64}
          monospace
        />
      )}

      {meta.key === 'identity.idExpirationWarningDays' && (
        <NumberField
          label="Warning window (days)"
          value={parseInteger(draft.value)}
          onChange={(n) => props.onValueChange(n === null ? '' : String(n))}
          min={0}
          max={3650}
          step={1}
          placeholder="e.g. 30"
          hint="0 disables the warning. Cap = 3650 (10 years)."
        />
      )}

      {meta.defaultBlurb && (
        <p className="critical-settings__hint">{meta.defaultBlurb}</p>
      )}

      <div className="critical-settings__row">
        <span className="critical-settings__row-label">Persisted</span>
        <ValueChip value={persistedValue} />
      </div>

      {draft.saveError && <Banner tone="err">{draft.saveError}</Banner>}
      {draft.savedNote && !draft.dirty && <Banner tone="info">{draft.savedNote}</Banner>}

      <div className="critical-settings__actions">
        <Button onClick={props.onRevert} disabled={!draft.dirty || draft.saving}>
          Revert
        </Button>
        <Button variant="primary" onClick={props.onSave} disabled={!draft.dirty || draft.saving}>
          {draft.saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function dataTypeFor(key: CriticalKey): string {
  switch (key) {
    case 'identity.ddp.enabled':
    case 'admin.auth.allowBypassKey':
      return 'bool';
    case 'identity.idExpirationWarningDays':
      return 'int';
    default:
      return 'string';
  }
}

function categoryFor(key: CriticalKey): string {
  if (key.startsWith('identity.')) return 'identity';
  if (key.startsWith('admin.')) return 'auth';
  if (key.startsWith('notifications.')) return 'notifications';
  return 'general';
}
